/**
 * Structure copied from ../finance/Receipts.tsx and ./List.tsx (loader reads paging, `q` and status
 * from the URL; `PagedTable` owns the frame) — vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-list/table.tsx
 * via ../invoicing/List.tsx — applied to Customers' newsletter subscribers. Gated by its nav entry
 * (`customers.read`), as the customer list is.
 */
import { Link, useLoaderData, useNavigate, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { Button } from "rizzui";
import { EmptyState } from "@steelyard/theme";
import { PagedTable, type StatusOption } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { SearchInput } from "../../components/table/search-input";
import { newsletterColumns } from "./newsletter-columns";
import { listNewsletterSubscribers } from "../../lib/services/customers";
import { hrefWith, readPaging } from "../../lib/paging";

const STATUSES = [
  { value: "subscribed", label: "Subscribed" },
  { value: "unsubscribed", label: "Unsubscribed" },
] as const;
type Status = (typeof STATUSES)[number]["value"];

export async function newsletterLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const { page, pageSize } = readPaging(params);
  const q = params.get("q") || undefined;
  // Only a status this screen offers reaches the API; a hand-edited one means "every status".
  const status = STATUSES.find((option) => option.value === params.get("status"))?.value as Status | undefined;

  return { result: await listNewsletterSubscribers({ page, pageSize, q, status }), q, status };
}

export default function NewsletterSubscribers() {
  const { result, q, status } = useLoaderData() as Awaited<ReturnType<typeof newsletterLoader>>;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const query = (over: Record<string, string | undefined>) => hrefWith("/customers/newsletter", searchParams, over);
  const filtered = Boolean(q || status);

  const statusOptions: StatusOption[] = [
    { label: "Every status", href: query({ status: undefined, page: undefined }), active: !status },
    ...STATUSES.map((option) => ({
      label: option.label,
      href: query({ status: option.value, page: undefined }),
      active: option.value === status,
      status: option.value,
    })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Newsletter subscribers"
        count={result.totalCount}
        description="Every address that signed up for the newsletter, and whether it still receives it."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Customers", href: "/customers" }, { name: "Newsletter subscribers" }]}
      />

      <PagedTable
        result={result}
        caption="Newsletter subscribers, with status, where they signed up and when"
        columns={newsletterColumns}
        getRowId={(row) => row.id}
        status={{ label: "Status", options: statusOptions }}
        // `q` is a server param, so this searches every subscriber, not the page on screen.
        search={<SearchInput value={q} placeholder="Search by email…" onSearch={(next) => navigate(query({ q: next, page: undefined }))} />}
        clear={{ active: filtered, href: query({ q: undefined, status: undefined, page: undefined }) }}
        empty={
          <EmptyState
            title="No subscribers matched"
            action={
              filtered ? (
                <Link to="/customers/newsletter">
                  <Button as="span" size="sm" variant="outline">
                    Clear the filters
                  </Button>
                </Link>
              ) : null
            }
          >
            {filtered ? "Nothing matched those filters." : "Nobody has subscribed to the newsletter yet."}
          </EmptyState>
        }
      />
    </div>
  );
}
